/**
 * 500 Internal Server Error Page (P2-1-2)
 *
 * サーバーエラー発生時に表示されるページ。
 * React Error Boundary からのフォールバックとしても使用可能。
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, RefreshCw, Home, ChevronDown, ChevronUp } from "lucide-react";

interface Error500Props {
  error?: Error | null;
  resetErrorBoundary?: () => void;
}

const Error500 = ({ error, resetErrorBoundary }: Error500Props) => {
  const [showDetails, setShowDetails] = useState(false);
  const [errorId] = useState(() => `ERR-${Date.now().toString(36).toUpperCase()}`);

  useEffect(() => {
    // エラーをコンソールにログ
    console.error("500 Error:", {
      errorId,
      message: error?.message,
      stack: error?.stack,
      timestamp: new Date().toISOString(),
    });
  }, [error, errorId]);

  const handleRetry = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            サーバーエラー
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            申し訳ありません。予期しないエラーが発生しました。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            問題が続く場合は、以下のエラーIDを添えてサポートにお問い合わせください。
          </p>

          <div className="rounded-lg bg-gray-100 dark:bg-gray-800 px-4 py-2 text-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">エラーID</span>
            <p className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
              {errorId}
            </p>
          </div>

          {error && (
            <div className="space-y-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex w-full items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-expanded={showDetails}
              >
                技術的な詳細
                {showDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
              {showDetails && (
                <div className="rounded-lg bg-gray-100 dark:bg-gray-800 p-3 text-xs font-mono text-gray-700 dark:text-gray-300 overflow-auto max-h-40">
                  <p className="font-semibold text-red-600 dark:text-red-400">
                    {error.message}
                  </p>
                  {error.stack && (
                    <pre className="mt-2 whitespace-pre-wrap text-gray-500 dark:text-gray-400">
                      {error.stack.split("\n").slice(0, 5).join("\n")}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button onClick={handleRetry} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              再試行
            </Button>
            <Button variant="outline" onClick={handleGoHome} className="flex-1">
              <Home className="mr-2 h-4 w-4" />
              ホームに戻る
            </Button>
          </div>

          <p className="text-center text-xs text-gray-400 dark:text-gray-500 pt-2">
            このエラーは自動的に報告されています
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Error500;
